import { platformApi } from '../api/axiosInstance';
import { Platform_Me_Url, Platform_Logout_Url } from '../api/endpoints';

// SK Drive owns NO authentication UI — the SK Platform (skplatform-fe) is the
// only place a user logs in, signs up, or resets a password. This module only
// talks to the platform's session endpoints; see platformAuthRedirect.ts for
// the handoff to the platform's auth screens. Mirrors sk-catalog-frontend's
// services/auth/auth.ts (getCurrentUser/logoutUser + me-cache).

export interface SessionUser {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  profile_complete?: boolean;
}

export interface SessionOrg {
  id: string;
  name?: string;
}

export interface SessionMembership {
  orgId: string;
  role: string;
}

export interface MeResponse {
  user: SessionUser;
  org: SessionOrg;
  memberships?: SessionMembership[];
}

const ME_TTL_MS = 5_000;
let meCache: { at: number; data: MeResponse } | null = null;
let meInflight: Promise<MeResponse> | null = null;

/** Bust the cached /v1/me result — call after anything that changes the session. */
export function invalidateMeCache(): void {
  meCache = null;
}

/**
 * GET /v1/me, cookie-authenticated. Cached for a few seconds with in-flight
 * de-duplication so the root gate and the session context (which both probe
 * on boot) share a single network call instead of racing two.
 */
export async function getCurrentUser(): Promise<MeResponse> {
  if (meCache && Date.now() - meCache.at < ME_TTL_MS) return meCache.data;
  if (meInflight) return meInflight;

  meInflight = platformApi
    .get<{ data: MeResponse }>(Platform_Me_Url)
    .then((r) => r.data.data)
    .then((data) => {
      meCache = { at: Date.now(), data };
      return data;
    })
    .finally(() => {
      meInflight = null;
    });

  return meInflight;
}

export async function logoutUser(): Promise<void> {
  try {
    await platformApi.post(Platform_Logout_Url);
  } finally {
    invalidateMeCache();
  }
}
